<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
    <div class="modal-content">
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">{{ isEditMode ? 'Edit Payment' : 'Add Payment' }}</h2>
        <button class="close-button" @click="closeModal">×</button>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <!-- Payment Account -->
        <div class="form-group">
          <label class="form-label">Payment Account <span class="required">*</span></label>
          <select v-model="formData.cashBankAccountID" class="form-control">
            <option value="">Select account</option>
            <option
              v-for="account in cashBankAccounts"
              :key="account.id"
              :value="account.id"
            >
              {{ account.accountName }} ({{ account.accountType }})
            </option>
          </select>
        </div>

        <!-- Payment Date -->
        <div class="form-group">
          <label class="form-label">Payment Date <span class="required">*</span></label>
          <input v-model="formData.paymentDate" type="date" class="form-control">
        </div>

        <!-- Vendor -->
        <div class="form-group">
          <label class="form-label">Vendor <span class="required">*</span></label>
          <select v-model="formData.partyID" class="form-control" @change="onVendorChange">
            <option value="">Select vendor</option>
            <option
              v-for="vendor in vendors"
              :key="vendor.partyID || vendor.id"
              :value="vendor.partyID || vendor.id"
            >
              {{ vendor.name }}
            </option>
          </select>
        </div>

        <!-- WHT Deduction -->
        <div class="form-group">
          <label class="form-label">WHT Deduction</label>
          <input v-model.number="formData.whtDeduction" type="number" min="0" step="0.01" placeholder="0" class="form-control">
        </div>

        <!-- Notes -->
        <div class="form-group">
          <label class="form-label">Notes (optional)</label>
          <textarea v-model="formData.notes" placeholder="Payment notes..." class="form-control notes-input"></textarea>
        </div>

        <!-- Select Expenses -->
        <div class="expenses-section">
          <div class="section-header">
            <h3 class="section-title">Select Expenses to Pay</h3>
            <button v-if="unpaidExpenses.length > 0" class="select-all-btn" @click="selectAllExpenses">
              {{ selectedExpenses.length === unpaidExpenses.length ? 'Deselect All' : 'Select All' }}
            </button>
          </div>

          <div v-if="!formData.partyID" class="empty-expenses">
            Select a vendor to see unpaid expenses
          </div>
          <div v-else-if="expenseLoading" class="loading-expenses">
            Loading expenses...
          </div>
          <div v-else-if="unpaidExpenses.length === 0" class="empty-expenses">
            No unpaid expenses for this vendor
          </div>

          <table v-else class="expenses-table">
            <thead>
              <tr>
                <th class="checkbox-col"><input type="checkbox" @change="toggleAllExpenses" :checked="selectedExpenses.length === unpaidExpenses.length && unpaidExpenses.length > 0"></th>
                <th>Description</th>
                <th>Date</th>
                <th>Outstanding</th>
                <th>Pay Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="expense in unpaidExpenses" :key="expense.id" class="expense-row">
                <td class="checkbox-col">
                  <input type="checkbox" :checked="selectedExpenses.includes(expense.id)" @change="onExpenseToggle(expense)">
                </td>
                <td>{{ expense.description }}</td>
                <td>{{ formatDate(expense.expenseDate || expense.date) }}</td>
                <td>${{ getOutstandingAmount(expense).toLocaleString() }}</td>
                <td class="pay-amount-col">
                  <input
                    v-if="selectedExpenses.includes(expense.id)"
                    type="number"
                    class="form-control pay-amount-input"
                    :value="paymentAmounts[expense.id]"
                    @input="onPayAmountInput(expense.id, $event)"
                    min="0.01"
                    :max="getOutstandingAmount(expense)"
                    step="0.01"
                  >
                  <span v-else class="pay-amount-disabled">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <div class="total-section" v-if="selectedExpenses.length > 0">
          <div class="total-line total-subtext">{{ selectedExpenses.length }} expense{{ selectedExpenses.length > 1 ? 's' : '' }} selected</div>
          <div class="total-line">
            Total: <strong>${{ calculateTotal().toLocaleString() }}</strong>
            <span v-if="formData.whtDeduction"> | WHT: <strong>${{ (formData.whtDeduction || 0).toLocaleString() }}</strong></span>
            <span v-if="formData.whtDeduction"> | Net: <strong>${{ (calculateTotal() - (formData.whtDeduction || 0)).toLocaleString() }}</strong></span>
          </div>
        </div>
        <div class="footer-actions">
          <button class="btn-cancel" @click="closeModal">Cancel</button>
          <button class="btn-primary" @click="handleSubmit" :disabled="paymentLoading">
            {{ paymentLoading ? (isEditMode ? 'Updating...' : 'Recording...') : (isEditMode ? 'Update Payment' : 'Record Payment') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'AddPaymentModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    payment: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      formData: {
        cashBankAccountID: '',
        paymentDate: new Date().toISOString().split('T')[0],
        partyID: '',
        whtDeduction: 0,
        notes: ''
      },
      selectedExpenses: [],
      paymentAmounts: {}
    }
  },
  computed: {
    ...mapGetters('vendor', ['vendors']),
    ...mapGetters('expense', ['unpaidExpenses']),
    ...mapGetters('expense', { expenseLoading: 'loading' }),
    ...mapGetters('payment', { paymentLoading: 'loading' }),
    isEditMode() {
      return !!this.payment
    },
    cashBankAccounts() {
      return this.$store.getters['cashbank/accounts']?.accounts || this.$store.getters['cashbank/accounts'] || []
    }
  },
  watch: {
    async isOpen(val) {
      if (val) {
        if (this.payment) {
          this.formData.cashBankAccountID = this.payment.cashBankAccountID || ''
          this.formData.paymentDate = this.payment.paymentDate ? this.payment.paymentDate.split('T')[0] : new Date().toISOString().split('T')[0]
          this.formData.partyID = this.payment.vendorPartyID || this.payment.partyID || ''
          this.formData.whtDeduction = this.payment.whtDeduction || 0
          this.formData.notes = this.payment.notes || ''
          this.selectedExpenses = this.payment.expenseIDs || []
        } else {
          this.resetForm()
        }
        await this.loadInitialData()
        if (this.payment && this.formData.partyID) {
          await this.fetchUnpaidExpenses(this.formData.partyID)
          this.populatePaymentAmounts()
        }
      }
    }
  },
  methods: {
    ...mapActions('vendor', ['fetchVendors']),
    ...mapActions('expense', ['fetchUnpaidExpenses']),
    ...mapActions('cashbank', ['fetchAccounts']),

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },

    async loadInitialData() {
      await Promise.all([
        this.fetchVendors(),
        this.fetchAccounts()
      ])
    },

    async onVendorChange() {
      this.selectedExpenses = []
      this.paymentAmounts = {}
      if (this.formData.partyID) {
        await this.fetchUnpaidExpenses(this.formData.partyID)
      }
    },

    closeModal() {
      this.$emit('close')
      this.resetForm()
    },

    getOutstandingAmount(expense) {
      return expense.remainingAmount != null ? expense.remainingAmount : (expense.amount || 0)
    },

    onExpenseToggle(expense) {
      const index = this.selectedExpenses.indexOf(expense.id)
      if (index === -1) {
        this.selectedExpenses.push(expense.id)
        this.$set(this.paymentAmounts, expense.id, this.getOutstandingAmount(expense))
      } else {
        this.selectedExpenses.splice(index, 1)
        this.$delete(this.paymentAmounts, expense.id)
      }
    },

    onPayAmountInput(expenseId, event) {
      const value = parseFloat(event.target.value)
      if (!isNaN(value) && value >= 0) {
        this.$set(this.paymentAmounts, expenseId, value)
      } else if (event.target.value === '') {
        this.$set(this.paymentAmounts, expenseId, 0)
      }
    },

    populatePaymentAmounts() {
      const amounts = {}
      this.selectedExpenses.forEach(id => {
        const expense = this.unpaidExpenses.find(e => e.id === id)
        if (expense) {
          amounts[id] = this.getOutstandingAmount(expense)
        }
      })
      this.paymentAmounts = amounts
    },

    selectAllExpenses() {
      if (this.selectedExpenses.length === this.unpaidExpenses.length) {
        this.selectedExpenses = []
        this.paymentAmounts = {}
      } else {
        this.selectedExpenses = this.unpaidExpenses.map(e => e.id)
        const amounts = {}
        this.unpaidExpenses.forEach(e => {
          amounts[e.id] = this.getOutstandingAmount(e)
        })
        this.paymentAmounts = amounts
      }
    },

    toggleAllExpenses() {
      this.selectAllExpenses()
    },

    handleSubmit() {
      if (!this.formData.cashBankAccountID || !this.formData.paymentDate || !this.formData.partyID || this.selectedExpenses.length === 0) {
        this.$store.dispatch('notification/showError', 'Please fill all required fields and select at least one expense')
        return
      }

      for (const expenseId of this.selectedExpenses) {
        const payAmount = this.paymentAmounts[expenseId] || 0
        const expense = this.unpaidExpenses.find(e => e.id === expenseId)
        if (!expense) continue

        if (payAmount <= 0) {
          this.$store.dispatch('notification/showError', `Payment amount for "${expense.description}" must be greater than 0`)
          return
        }

        const outstanding = this.getOutstandingAmount(expense)
        if (payAmount > outstanding) {
          this.$store.dispatch('notification/showError', `Payment amount for "${expense.description}" cannot exceed outstanding amount ($${outstanding.toLocaleString()})`)
          return
        }
      }

      const paymentData = {
        cashBankAccountID: parseInt(this.formData.cashBankAccountID),
        paymentDate: new Date(this.formData.paymentDate).toISOString(),
        vendorPartyID: parseInt(this.formData.partyID),
        expenses: this.selectedExpenses.map(id => ({
          expenseID: id,
          amount: this.paymentAmounts[id] || 0
        })),
        whtDeduction: parseFloat(this.formData.whtDeduction) || 0,
        notes: this.formData.notes || null
      }

      if (this.isEditMode) {
        this.$emit('update', { id: this.payment.id, payload: paymentData })
      } else {
        this.$emit('create', paymentData)
      }
    },

    calculateTotal() {
      return this.selectedExpenses.reduce((sum, id) => {
        return sum + (this.paymentAmounts[id] || 0)
      }, 0)
    },

    resetForm() {
      this.formData = {
        cashBankAccountID: '',
        paymentDate: new Date().toISOString().split('T')[0],
        partyID: '',
        whtDeduction: 0,
        notes: ''
      }
      this.selectedExpenses = []
      this.paymentAmounts = {}
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  max-width: 700px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.required {
  color: #ef4444;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
}

.form-control:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.notes-input {
  resize: vertical;
  min-height: 60px;
  font-family: inherit;
}

/* Expenses Section */
.expenses-section {
  margin-top: 28px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

.select-all-btn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
}

.empty-expenses,
.loading-expenses {
  padding: 24px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 8px;
}

.expenses-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.expenses-table th {
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.expenses-table td {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
}

.checkbox-col {
  width: 40px;
  text-align: center;
}

.expense-row:hover {
  background: #f9fafb;
}

input[type="checkbox"] {
  cursor: pointer;
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.total-section {
  font-size: 14px;
  color: #1f2937;
}

.footer-actions {
  display: flex;
  gap: 12px;
}

.btn-cancel {
  background: white;
  color: #1f2937;
  border: 1px solid #d1d5db;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-cancel:hover {
  background: #f3f4f6;
}

.btn-primary {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.pay-amount-col {
  width: 130px;
}

.pay-amount-input {
  width: 110px !important;
  padding: 6px 8px !important;
  font-size: 13px !important;
  text-align: right;
}

.pay-amount-disabled {
  color: #9ca3af;
  font-size: 13px;
}

.total-line {
  margin-bottom: 4px;
}

.total-line:last-child {
  margin-bottom: 0;
}

.total-subtext {
  font-size: 12px;
  color: #6b7280;
}
</style>
